import type { Species } from './types';

export const CONFIG_VERSION = 1 as const;
export const STORAGE_KEY = 'bnff.config.v1';
export const SAVE_DEBOUNCE_MS = 200;

export interface SpeciesConfig {
    count: number;
    sizeMult: number;
    speedMult: number;
    aggression: number;
}

export interface GlobalConfig {
    aggression: number;
    shoaling: number;
    predatorRatio: number;
    preyRatio: number;
    boundaryMargin: number;
    boundaryForce: number;
    frameMs: number;
    slowmoFactor: number;
    lookRangeMult: number;
    wanderJitter: number;
    showBehavior: boolean;
    lifecycleEnabled: boolean;
    censusEnabled: boolean;
    absorbRatio: number;
}

export type Wall = [number, number, number, number];

export interface Config {
    version: typeof CONFIG_VERSION;
    global: GlobalConfig;
    species: Record<Species, SpeciesConfig>;
    walls: Wall[];
}

export interface SpeciesMeta {
    defaultCount: number;
    sizeBase: number;
    color: string;
    growthRate: number;
    massCap: number;
}

export const SPECIES_META = Object.freeze({
    minnow: Object.freeze({
        defaultCount: 25,
        sizeBase: 0.6,
        color: '#1a0dab',
        growthRate: 0.005,
        massCap: 1.0,
    }),
    tetra: Object.freeze({
        defaultCount: 12,
        sizeBase: 0.9,
        color: '#000000',
        growthRate: 0.005,
        massCap: 1.5,
    }),
    bass: Object.freeze({
        defaultCount: 6,
        sizeBase: 1.4,
        color: '#444444',
        growthRate: 0.01,
        massCap: 2.5,
    }),
    pike: Object.freeze({
        defaultCount: 2,
        sizeBase: 2.0,
        color: '#7a0010',
        growthRate: 0.02,
        massCap: 4.0,
    }),
}) satisfies Readonly<Record<Species, SpeciesMeta>>;

export type DeepPartial<T> = T extends readonly unknown[]
    ? T
    : T extends object
      ? { [K in keyof T]?: DeepPartial<T[K]> }
      : T;

function buildDefaults(): Config {
    return {
        version: CONFIG_VERSION,
        global: {
            aggression: 1,
            shoaling: 1,
            predatorRatio: 2,
            preyRatio: 0.5,
            boundaryMargin: 50,
            boundaryForce: 3,
            frameMs: 16.67,
            slowmoFactor: 1,
            lookRangeMult: 100,
            wanderJitter: 0.05,
            showBehavior: false,
            lifecycleEnabled: false,
            censusEnabled: false,
            absorbRatio: 2,
        },
        species: {
            minnow: {
                count: SPECIES_META.minnow.defaultCount,
                sizeMult: 1,
                speedMult: 1,
                aggression: 1,
            },
            tetra: {
                count: SPECIES_META.tetra.defaultCount,
                sizeMult: 1,
                speedMult: 1,
                aggression: 1,
            },
            bass: {
                count: SPECIES_META.bass.defaultCount,
                sizeMult: 1,
                speedMult: 1,
                aggression: 1,
            },
            pike: {
                count: SPECIES_META.pike.defaultCount,
                sizeMult: 1,
                speedMult: 1,
                aggression: 1,
            },
        },
        walls: [],
    };
}

function deepFreeze<T>(value: T): T {
    if (value !== null && typeof value === 'object') {
        for (const v of Object.values(value as Record<string, unknown>)) {
            deepFreeze(v);
        }
        Object.freeze(value);
    }
    return value;
}

export const DEFAULTS: Config = deepFreeze(buildDefaults());

function clone(cfg: Config): Config {
    return JSON.parse(JSON.stringify(cfg)) as Config;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.getPrototypeOf(value) === Object.prototype
    );
}

function deepMerge<T>(base: T, partial: DeepPartial<T>): T {
    if (!isPlainObject(base) || !isPlainObject(partial)) {
        return (partial as unknown as T) ?? base;
    }
    const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
    for (const key of Object.keys(partial)) {
        const pv = (partial as Record<string, unknown>)[key];
        const bv = out[key];
        if (isPlainObject(bv)) {
            if (isPlainObject(pv)) {
                out[key] = deepMerge(bv, pv as DeepPartial<typeof bv>);
            }
        } else if (pv !== undefined) {
            out[key] = pv;
        }
    }
    return out as T;
}

type GlobalNumericKey = {
    [K in keyof GlobalConfig]: GlobalConfig[K] extends number ? K : never;
}[keyof GlobalConfig];

const GLOBAL_NUMERIC_RANGES = {
    aggression: [0, 3],
    shoaling: [0, 2],
    predatorRatio: [1, 5],
    preyRatio: [0.1, 1],
    boundaryMargin: [0, 200],
    boundaryForce: [0, 10],
    frameMs: [8, 50],
    slowmoFactor: [0.5, 3],
    lookRangeMult: [50, 300],
    wanderJitter: [0, 0.5],
    absorbRatio: [1, 5],
} as const satisfies Record<GlobalNumericKey, readonly [number, number]>;

const GLOBAL_BOOLEAN_KEYS = [
    'showBehavior',
    'lifecycleEnabled',
    'censusEnabled',
] as const satisfies readonly (keyof GlobalConfig)[];

const SPECIES_NUMERIC_RANGES = {
    count: [0, 60],
    sizeMult: [0.5, 2],
    speedMult: [0.5, 2],
    aggression: [0, 3],
} as const satisfies Record<keyof SpeciesConfig, readonly [number, number]>;

const SPECIES_INTEGER_KEYS = ['count'] as const satisfies readonly (keyof SpeciesConfig)[];

export const WALLS_MAX = 256;
export const WALL_COORD_MIN = -32768;
export const WALL_COORD_MAX = 32767;

function isFiniteNumberInRangeOrAbsent(v: unknown, range: readonly [number, number]): boolean {
    if (v === undefined) return true;
    if (typeof v !== 'number' || !Number.isFinite(v)) return false;
    return v >= range[0] && v <= range[1];
}

function isBooleanOrAbsent(v: unknown): boolean {
    return v === undefined || typeof v === 'boolean';
}

function validateConfig(value: unknown): value is Config {
    if (!isPlainObject(value)) return false;
    const v = value;
    if (v.version !== CONFIG_VERSION) return false;
    if (!isPlainObject(v.global)) return false;
    if (!isPlainObject(v.species)) return false;
    if (!Array.isArray(v.walls)) return false;
    for (const key of Object.keys(
        GLOBAL_NUMERIC_RANGES,
    ) as (keyof typeof GLOBAL_NUMERIC_RANGES)[]) {
        if (!isFiniteNumberInRangeOrAbsent(v.global[key], GLOBAL_NUMERIC_RANGES[key])) return false;
    }
    for (const key of GLOBAL_BOOLEAN_KEYS) {
        if (!isBooleanOrAbsent(v.global[key])) return false;
    }
    for (const entry of Object.values(v.species)) {
        if (!isPlainObject(entry)) return false;
        for (const key of Object.keys(
            SPECIES_NUMERIC_RANGES,
        ) as (keyof typeof SPECIES_NUMERIC_RANGES)[]) {
            if (!isFiniteNumberInRangeOrAbsent(entry[key], SPECIES_NUMERIC_RANGES[key]))
                return false;
        }
        for (const key of SPECIES_INTEGER_KEYS) {
            const n = entry[key];
            if (n !== undefined && !Number.isInteger(n)) return false;
        }
    }
    if (v.walls.length > WALLS_MAX) return false;
    for (const wall of v.walls) {
        if (!Array.isArray(wall) || wall.length !== 4) return false;
        for (const n of wall) {
            if (typeof n !== 'number' || !Number.isFinite(n)) return false;
            if (n < WALL_COORD_MIN || n > WALL_COORD_MAX) return false;
        }
    }
    return true;
}

function tryGetItem(key: string): string | null {
    try {
        if (typeof localStorage === 'undefined') return null;
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function trySetItem(key: string, value: string): void {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(key, value);
        }
    } catch {
        /* quota / unavailable */
    }
}

function tryRemoveItem(key: string): void {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(key);
        }
    } catch {
        /* unavailable */
    }
}

export function loadConfig(): Config {
    const raw = tryGetItem(STORAGE_KEY);
    if (raw === null) return clone(DEFAULTS);
    try {
        const parsed: unknown = JSON.parse(raw);
        if (validateConfig(parsed)) {
            return deepMerge<Config>(clone(DEFAULTS), parsed);
        }
        return clone(DEFAULTS);
    } catch {
        return clone(DEFAULTS);
    }
}

let _current: Config | null = null;
let _saveTimer: ReturnType<typeof setTimeout> | null = null;
let _sceneActive = false;
const _subscribers = new Set<(cfg: Config) => void>();

export function getConfig(): Config {
    _current ??= loadConfig();
    return _current;
}

export function saveConfig(cfg: Config): void {
    if (_saveTimer !== null) clearTimeout(_saveTimer);
    const snapshot = JSON.stringify(cfg);
    _saveTimer = setTimeout(() => {
        _saveTimer = null;
        trySetItem(STORAGE_KEY, snapshot);
    }, SAVE_DEBOUNCE_MS);
}

export function setConfig(partial: DeepPartial<Config>): void {
    const merged = deepMerge<Config>(getConfig(), partial);
    _current = merged;
    if (!_sceneActive) saveConfig(merged);
    for (const fn of _subscribers) fn(merged);
}

export function applySceneConfig(partial: DeepPartial<Config>): void {
    const merged = deepMerge<Config>(clone(DEFAULTS), partial);
    _current = merged;
    _sceneActive = true;
    for (const fn of _subscribers) fn(merged);
}

export function isSceneActive(): boolean {
    return _sceneActive;
}

export function subscribe(fn: (cfg: Config) => void): () => void {
    _subscribers.add(fn);
    return () => {
        _subscribers.delete(fn);
    };
}

export function resetConfig(): Config {
    if (_saveTimer !== null) {
        clearTimeout(_saveTimer);
        _saveTimer = null;
    }
    tryRemoveItem(STORAGE_KEY);
    const fresh = clone(DEFAULTS);
    _current = fresh;
    _sceneActive = false;
    for (const fn of _subscribers) fn(fresh);
    return fresh;
}

export function _resetState(): void {
    if (_saveTimer !== null) {
        clearTimeout(_saveTimer);
        _saveTimer = null;
    }
    _current = null;
    _sceneActive = false;
    _subscribers.clear();
}
